import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronRight, Info, ShieldAlert } from "lucide-react";
import { cn } from "../../../../utils/cn";
import {
  HISTORY_STATE_LABELS,
  SEVERITY_TONE,
  describeDisconnect,
  formatDisconnectTime,
  isSessionDown,
  reasonText,
} from "../../../../utils/waDisconnect";

const SEVERITY_ICON = { error: ShieldAlert, warning: AlertTriangle, info: Info };

/**
 * Por qué se cayó la sesión de un número de WhatsApp no oficial y, para quien
 * administra los números, el historial de conexión desplegable.
 *
 * - Sesión caída: el motivo manda (ícono + texto, color por gravedad).
 * - Sesión conectada: la última caída queda como dato secundario.
 * - `connection_history` solo llega del backend para admin/supervisor;
 *   `showHistory` repite ese gate en la UI.
 */
export default function WaConnectionDiagnostics({ integration, isConnected, showHistory }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const reason = describeDisconnect(integration?.last_disconnect);
  const history = showHistory && Array.isArray(integration?.connection_history)
    ? integration.connection_history
    : [];
  if (!reason && history.length === 0) return null;

  // A past outage (the session reconnected since) never reads as the current
  // reason, even if the session is down again for another cause.
  // Same rule as the rail: only a session that is down (not connecting, e.g.
  // while re-linking by QR) shows the reason as current.
  const current =
    !isConnected && isSessionDown(integration?.wa_connection_status) && !!reason?.ongoing;
  const tone = SEVERITY_TONE[reason?.severity] || SEVERITY_TONE.info;
  const ReasonIcon = current ? SEVERITY_ICON[reason?.severity] || Info : Info;
  const when = reason?.at ? formatDisconnectTime(reason.at, i18n?.language) : "";

  return (
    <div className="flex flex-col gap-1.5">
      {reason && (
        <div className="flex items-start gap-1.5 text-[12px] md:text-[11px] leading-snug">
          <ReasonIcon
            data-testid="wa-disconnect-icon"
            className={cn("w-3.5 h-3.5 shrink-0 mt-px", current ? tone.text : "text-fg-subtle")}
            aria-hidden="true"
          />
          <p className="min-w-0 text-fg-muted">
            <span className={cn(current ? "font-medium text-fg" : "text-fg-muted")}>
              {current ? `${t("Motivo")}: ` : `${t("Última desconexión")}: `}
              {reasonText(reason, t)}
            </span>
            {when && (
              <span className="text-fg-subtle tabular-nums">
                {" · "}
                <time dateTime={reason.at.toISOString()}>{when}</time>
              </span>
            )}
          </p>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls={panelId}
            className="inline-flex items-center gap-1 min-h-[44px] md:min-h-0 md:h-6 -ml-1 px-1 rounded text-[12px] md:text-[11px] font-medium text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            <ChevronRight
              className={cn(
                "w-3 h-3 transition-transform duration-200 ease-out motion-reduce:transition-none",
                open && "rotate-90",
              )}
              aria-hidden="true"
            />
            {t("Historial de conexión ({{n}})", { n: history.length })}
          </button>
          {open && (
            <ol
              id={panelId}
              aria-label={t("Historial de conexión")}
              className="mt-1 ml-1.5 pl-3 border-l border-border flex flex-col gap-1"
            >
              {history.map((entry, index) => {
                const entryReason = entry?.reason_key ? describeDisconnect(entry) : null;
                const entryTone =
                  entry?.state === "open"
                    ? "bg-status-success"
                    : entryReason
                    ? (SEVERITY_TONE[entryReason.severity] || SEVERITY_TONE.info).dot
                    : "bg-fg-subtle";
                const entryWhen = formatDisconnectTime(entry?.at, i18n?.language);
                const stateKey = HISTORY_STATE_LABELS[entry?.state];
                return (
                  <li
                    key={`${entry?.at || "x"}-${index}`}
                    className="flex items-baseline gap-2 text-[12px] md:text-[11px] leading-snug"
                  >
                    <span
                      className={cn("shrink-0 w-1.5 h-1.5 rounded-full translate-y-[-1px]", entryTone)}
                      aria-hidden="true"
                    />
                    <span className="shrink-0 min-w-[5.5rem] whitespace-nowrap text-fg-subtle tabular-nums">
                      {entryWhen || "—"}
                    </span>
                    <span className="min-w-0 text-fg-muted">
                      <span className="text-fg">{stateKey ? t(stateKey) : entry?.state}</span>
                      {entryReason && <> · {reasonText(entryReason, t)}</>}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
